import { Component, Input, OnInit, SkipSelf, ViewChild, ViewContainerRef } from "@angular/core";
import { ControlContainer, UntypedFormGroup } from "@angular/forms";
import {
  CostEstimation,
  CostEstimationFormBuilder,
} from "src/app/feature-module/cost-estimations/models/CostEstimation";
import { AttachDocumentsComponent } from "src/app/feature-module/documents/component/attach-documents/attach-documents.component";
import { ReferenceType } from "src/app/shared/model/ReferenceType";
import { CheckCostEstimationResult } from "../../models/CheckCostEstimationResult";
import { CostEstimationPart } from "../../models/CostEstimationPart";
import { RepairService } from "src/app/feature-module/repair/services/repair.service";
import { SearchRepairsCriteriaFormBuilder } from "src/app/feature-module/repair/models/SearchRepairsCriteria";
import { RepairStatus } from "src/app/feature-module/repair/models/RepairStatus";
import { ClaimDataService } from "src/app/feature-module/claims-data/service/claim-data.service";
import { CustomDialogService } from "src/app/shared/services/custom-dialog.service";
import { CustomDialogSettings } from "src/app/shared/model/CustomDialogSettings";
import { DialogAlignment } from "src/app/shared/model/DialogAlignment";
import { OrderingPartyInfoComponent } from "src/app/feature-module/ordering-party/components/ordering-party-info/ordering-party-info.component";
import { AuthService } from "src/app/core/authentication/auth.service";
import { Abilities } from "src/app/shared/model/Abilities";
import { CostEstimationType } from 'src/app/feature-module/cost-estimations/models/CostEstimationType';

@Component({
  selector: "app-cost-estimation-form",
  templateUrl: "./cost-estimation-form.component.html",
  styleUrls: ["./cost-estimation-form.component.scss"],
  viewProviders: [
    {
      provide: ControlContainer,
      useFactory: (container: ControlContainer) => container,
      deps: [[new SkipSelf(), ControlContainer]],
    },
  ],
})
export class CostEstimationFormComponent implements OnInit {

  constructor(
    private repairService: RepairService,
    private claimDataService: ClaimDataService,
    private customDialogService: CustomDialogService,
    private authService: AuthService
  ) {}

  @ViewChild(AttachDocumentsComponent)
  attachedFileComponent: AttachDocumentsComponent;
  @ViewChild('costEstimationFormDialogContainer', { read: ViewContainerRef })
  containerRef!: ViewContainerRef;
  @Input() form: UntypedFormGroup;

  editMode: boolean;
  showMarkers = false;
  costEstimation: CostEstimation;
  costEstimationReferenceType = ReferenceType.CostEstimation;
  costEstimationParts: CostEstimationPart[];
  ongoingRepairWarning = false;
  canHaveAccessToOrderingPartyInfo = false;

  error: string;
  netCheckResult: CheckCostEstimationResult;
  acceptedNetCheckResult: CheckCostEstimationResult;
  forInvoiceNetCheckResult: CheckCostEstimationResult;
  forInvoicePartialNetCheckResult: CheckCostEstimationResult;

  ngOnInit(): void {
    this.canHaveAccessToOrderingPartyInfo = this.authService.hasAbility(Abilities.CanHaveAccessToOrderingPartyInfo);
    this.costEstimationParts = this.form.controls["costEstimationParts"].value;
    this.costEstimation = this.form.getRawValue();
    this.editMode = this.costEstimation.id > 0;
    
    this.showMarkers = this.costEstimation?.costEstimationTypeId == CostEstimationType.Standard;

    this.checkCostEstimationValues();
    this.checkAcceptedCostEstimationValues();
    this.checkForInvoiceCostEstimationValues();
  }

  showOrderingPartyInfo(): void {
    if (!this.costEstimation?.claimIdentifier) {
      return;
    }

    this.claimDataService.getClaimData(this.costEstimation.claimIdentifier).subscribe(claim => {
      if (!claim?.claimOrderingPartyId) {
        return;
      }

      const dialogSettings: CustomDialogSettings = {
        content: OrderingPartyInfoComponent,
        minHeight: '10vh',
        maxHeight: '80vh',
        minWidth: '20vw',
        maxWidth: '60vw',
        appendTo: this.containerRef,
        dialogAlignment: DialogAlignment.top,
        dialogInputs: {
          orderingPartyId: claim.claimOrderingPartyId,
        },
      };

      this.customDialogService.showDialog(dialogSettings);
    });
  }

  copyAcceptedValues() {
    CostEstimationFormBuilder.CopyValuesToAccepted(this.form);
    this.checkAcceptedCostEstimationValues();
  }

  copyForInvoiceValues() {
    CostEstimationFormBuilder.CopyValuesToForInvoice(this.form);
    this.checkForInvoiceCostEstimationValues();
  }

  calculateForInvoiceValues() {
    CostEstimationFormBuilder.CalculateForInvoiceValues(this.form);
    this.checkForInvoiceCostEstimationValues();
  }

  copyForInvoicePartialValues() {
    CostEstimationFormBuilder.CopyValuesToForInvoicePartial(this.form);
    this.checkForInvoicePartialCostEstimationValues();
  }

  cleanForInvoicePartialValues() {
    CostEstimationFormBuilder.CleanValuesToForInvoicePartial(this.form);
    this.checkForInvoicePartialCostEstimationValues();
  }



  calculateNormaliesForInvoice() {
    const original = this.form.controls['originalPartsForInvoice']?.value || 0;
    const alternative = this.form.controls['alternativePartsForInvoice']?.value || 0;
    const control = this.form.controls['normaliesForInvoice'];
    control.setValue(Math.round((original + alternative) * 2) / 100);
    control.markAsDirty();
    this.checkForInvoiceCostEstimationValues();
  }

  checkCostEstimationValues() {
    this.netCheckResult = CostEstimationFormBuilder.CheckNetSumValue(this.form);
  }

  checkAcceptedCostEstimationValues() {
    this.acceptedNetCheckResult =
      CostEstimationFormBuilder.CheckAcceptedNetSumValue(this.form);
  }

  checkForInvoiceCostEstimationValues() {
    this.forInvoiceNetCheckResult =
      CostEstimationFormBuilder.CheckForInvoiceNetSumValue(this.form);
  }

  checkForInvoicePartialCostEstimationValues() {
    this.forInvoicePartialNetCheckResult =
      CostEstimationFormBuilder.CheckForInvoicePartialNetSumValue(this.form);
  }

  applyDiscountOutOfCostEstimationChanged() {
    this.checkCostEstimationValues();
    this.checkAcceptedCostEstimationValues();
    this.checkForInvoiceCostEstimationValues();
    this.checkForInvoicePartialCostEstimationValues();
  }

  isInvoicePartialNeededChanged($event: any) {
    
    if (!$event.target.checked) {
      this.cleanForInvoicePartialValues();
    }
  }

  forInvoiceConfirmationChanged() {

     this.ongoingRepairWarning = false;

    if(this.form.controls["forInvoiceConfirmation"].value && !this.costEstimation.forInvoiceConfirmation) {
      
      const criteria = SearchRepairsCriteriaFormBuilder.initSearchRepairsCriteria();
      criteria.claimIdentifier = this.costEstimation.claimIdentifier;
      criteria.repairStatusIds = [RepairStatus.InProgress, RepairStatus.New];
      
      this.repairService.searchRepairs(criteria).subscribe(repairs => {
        if (repairs.length > 0) {
          this.ongoingRepairWarning = true;
        }
      });
    }

  }
}
